import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { userService, UserUpdateRequest } from '../services/userService';
import './ProfileEdit.css';

const ProfileEdit: React.FC = () => {
    const { t } = useTranslation();
    const { userType } = useAuth();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState<UserUpdateRequest>({
        firstName: '',
        lastName: '',
        email: '',
        birthdate: '',
        password: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userData = await userService.getCurrentUser();
                setUser(userData);
                setFormData({
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    email: userData.email || '',
                    birthdate: userData.birthdate ? new Date(userData.birthdate).toISOString().split('T')[0] : '',
                    password: '',
                });
            } catch (error) {
                setMessage({ type: 'error', text: t('profile.updateError') });
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [t]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'confirmPassword') {
            setConfirmPassword(value);
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (formData.password && formData.password !== confirmPassword) {
            setMessage({ type: 'error', text: t('profile.passwordMismatch') });
            return;
        }

        try {
            const updateData: UserUpdateRequest = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                birthdate: formData.birthdate ? new Date(formData.birthdate).toISOString() : undefined,
            };

            if (formData.password) {
                updateData.password = formData.password;
            }

            await userService.updateProfile(updateData);
            setMessage({ type: 'success', text: t('profile.updateSuccess') });
            // Optionally refresh user data here if AuthContext supports it
        } catch (error) {
            setMessage({ type: 'error', text: t('profile.updateError') });
        }
    };

    if (loading) {
        return (
            <div className="profile-edit-container">
                <h2>{t('profile.title')}</h2>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="profile-edit-container">
            <h2>{t('profile.title')}</h2>
            {message && <div className={`message ${message.type}`}>{message.text}</div>}
            <form onSubmit={handleSubmit} className="profile-form">
                <div className="form-group">
                    <label>{t('common.username')}</label>
                    <input type="text" value={user?.userName || ''} disabled className="disabled-input" />
                </div>
                {userType !== 'SUPER_ADMIN' && (
                <div className="form-group">
                    <label>{t('common.firstName')}</label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                    />
                </div>)}

                {userType !== 'SUPER_ADMIN' && (<div className="form-group">
                    <label>{t('common.lastName')}</label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                    />
                </div>)}
                {userType === 'ADMIN' && (
                    <div className="form-group">
                        <label>{t('common.email')}</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>
                )}

                {userType === 'STUDENT' && (
                    <div className="form-group">
                        <label>{t('common.birthdate')}</label>
                        <input
                            type="date"
                            name="birthdate"
                            value={formData.birthdate}
                            onChange={handleChange}
                        />
                    </div>
                )}

                <div className="form-group">
                    <label>{t('common.password')}</label>
                    <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={t('profile.leaveEmptyToKeep')}
                    />
                </div>

                <div className="form-group">
                    <label>{t('common.confirmPassword')}</label>
                    <input
                        type="password"
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={handleChange}
                    />
                </div>

                <button type="submit" className="save-btn">{t('common.save')}</button>
            </form>
        </div>
    );
};

export default ProfileEdit;
